import { CommonUtil } from '@utils/common/common';
import { integerFormatter, percentFormatter } from '@utils/common/formatter';
import moment from 'moment';
import { gridOptionModel } from '@models/gridModel';
import {
  trackedKeywordModel,
  suggestPopupKeywordModel,
  competitorKeywordModel,
  IAnalysisKeywordType,
  ITestResultKeywordType,
  ICompetitorKeywordType,
  filterObjType,
} from '@models/keyword/keywordModel';
import colormap from 'colormap';

// gradient 색상 배열 변수
const colorGradientArr = colormap({
  colormap: 'YIGnBu',
  nshades: 200,
  format: 'hex',
  alpha: 2,
});

export class KeywordOptimizationGridOption {
  searchBox(responseData, groupList, groupHandler, addDelHandler) {
    const _data: ITestResultKeywordType[] = CommonUtil.getArrayByValidate(responseData);
    const columns = [
      {
        name: 'Keyword',
        selector: (row) => row.keyword,
        sortable: true,
      },
      {
        name: 'Search Score',
        selector: (row) => row.searchscore,
        sortable: true,
      },
      {
        name: 'Group',
        cell: (row) => {
          return (
            <>
              <label className="select">
                <select
                  name="score"
                  onChange={(e) => groupHandler(e, row.keyword, row.tracked, row.id)}
                  value={row.group === null ? 'nothing' : row.group}
                  disabled={row.tracked}
                >
                  <option value={'nothing'}>{'Select Group'}</option>
                  {groupList &&
                    groupList.length > 0 &&
                    groupList.map((item, index) => (
                      <option key={item.id} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                </select>
              </label>
            </>
          );
        },
        selector: (row) => row.group,
        sortable: true,
      },
      {
        name: 'Track(Add/Delete)',
        selector: (row) => row.tracked,
        cell: (row, index) => {
          return (
            <div>
              {row.tracked ? (
                <div className="track-ranking-chk">
                  <button
                    type="button"
                    className="btn-ico-single delete"
                    title="delete"
                    onClick={() => addDelHandler(row.keyword, 'del', row.group, row.tracked, row.id)}
                  >
                    <span className="blind">delete</span>
                  </button>
                </div>
              ) : (
                <div className="track-ranking-chk">
                  <input
                    type="checkbox"
                    checked={false}
                    className="ipt-check"
                    id={`checked-${index}`}
                    onChange={() => addDelHandler(row.keyword, 'add', row.group, row.tracked, row.id)}
                  />
                  <label htmlFor={`checked-${index}`}>
                    <span className="blind">미선택</span>
                  </label>
                </div>
              )}
            </div>
          );
        },
        sortable: true,
      },
    ];

    return {
      data: _data,
      columns,
    };
  }

  analysisBox(responseData, addDelHandler) {
    const _data: IAnalysisKeywordType[] = CommonUtil.getArrayByValidate(responseData);
    // 컬럼 정의 로직
    let columns = [
      {
        name: 'Keyword',
        selector: (row) => row.keyword,
        sortable: true,
      },
      {
        name: 'Add/Del',
        selector: (row) => row.tracked,
        cell: (row, index, column, id) => (
          <>
            {row.tracked ? (
              <button
                type="button"
                className="btn-ico-single delete"
                title="delete"
                onClick={() =>
                  addDelHandler({
                    keywordId: row.id,
                    keyword: row.keyword,
                    currentTracked: row.tracked,
                  })
                }
              >
                <span className="blind">delete</span>
              </button>
            ) : (
              <>
                <input
                  type="checkbox"
                  className="ipt-check"
                  id={`checked-table-${row.id}`}
                  checked={false} // 퍼블리싱은 V 처리를 했지만.. 여기선 제외시킨다.
                  onChange={() =>
                    addDelHandler({
                      keywordId: row.id,
                      keyword: row.keyword,
                      currentTracked: row.tracked,
                    })
                  }
                />
                <label htmlFor={`checked-table-${row.id}`}>
                  <span className="blind">미선택</span>
                </label>
              </>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: 'Average',
        selector: (row) => row.averageCount,
        cell: (row, index, column, id) => <div>{`${row.averageCount}(${percentFormatter(row.averagePercent)})`}</div>,
      },
    ];
    let gridData = [];

    if (_data.length > 0) {
      let tempAppArr = _data[0].appObj;

      tempAppArr.forEach((appObj, index) => {
        let temp = {
          name: (
            <div>
              <img src={appObj.appIcon} />
            </div>
          ),
          selector: (row) => row[`${appObj.appText}_count`],
          cell: (row, index, column, id) => {
            return (
              <div>{`${row[`${appObj.appText}_count`]}(${percentFormatter(row[`${appObj.appText}_percent`])})`}</div>
            );
          },
          conditionalCellStyles: [
            {
              when: (row) => row[`${appObj.appText}_percent`] < 10,
              style: {
                backgroundColor: `${colorGradientArr[200]}`,
              },
            },
            {
              when: (row) => 10 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 20,
              style: {
                backgroundColor: `${colorGradientArr[190]}`,
              },
            },
            {
              when: (row) => 20 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 30,
              style: {
                backgroundColor: `${colorGradientArr[180]}`,
              },
            },
            {
              when: (row) => 30 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 40,
              style: {
                backgroundColor: `${colorGradientArr[170]}`,
              },
            },
            {
              when: (row) => 40 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 50,
              style: {
                backgroundColor: `${colorGradientArr[160]}`,
              },
            },
            {
              when: (row) => 50 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 60,
              style: {
                backgroundColor: `${colorGradientArr[150]}`,
              },
            },
            {
              when: (row) => 60 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 70,
              style: {
                backgroundColor: `${colorGradientArr[140]}`,
              },
            },
            {
              when: (row) => 70 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 80,
              style: {
                backgroundColor: `${colorGradientArr[130]}`,
              },
            },
            {
              when: (row) => 80 < row[`${appObj.appText}_percent`] && row[`${appObj.appText}_percent`] < 90,
              style: {
                backgroundColor: `${colorGradientArr[120]}`,
              },
            },
            {
              when: (row) => 100 < row[`${appObj.appText}_percent`],
              style: {
                backgroundColor: `${colorGradientArr[110]}`,
              },
            },
          ],
          sortable: true,
        };
        //@ts-ignore
        columns.push(temp);
      });

      // 데이터 재정의
      gridData = _data.map((obj) => {
        let tempObj = Object.assign({}, obj);
        tempObj['appObj'].forEach((appObj) => {
          tempObj[`${appObj.appText}_count`] = appObj.count;
          tempObj[`${appObj.appText}_percent`] = appObj.percent * 100;
        });
        return tempObj;
      });
    }

    return {
      data: gridData,
      columns,
    };
  }

  competitorBox(responseData, filterObj?: filterObjType) {
    const _data: ICompetitorKeywordType[] = CommonUtil.getArrayByValidate(responseData);
    const columns = [
      {
        name: 'Keywords',
        selector: (row) => row.keyword,
        sortable: true,
      },
      {
        name: 'Search Score',
        selector: (row) => row.searchscore,
        sortable: true,
      },
      {
        name: 'Rank',
        selector: (row) => row.rank,
        sortable: true,
      },
      {
        name: 'Title',
        selector: (row) => row.title,
        cell: (row, index, column, id) => (
          <>
            {row.title ? (
              <span className="icon-included">
                <img src="/images/svg/circle-ok.svg" alt="포함" />
              </span>
            ) : (
              <span className="icon-included">
                <img src="/images/svg/circle-no.svg" alt="비포함" />
              </span>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: 'Sub',
        selector: (row) => row.sub,
        cell: (row, index, column, id) => (
          <>
            {row.sub ? (
              <span className="icon-included">
                <img src="/images/svg/circle-ok.svg" alt="포함" />
              </span>
            ) : (
              <span className="icon-included">
                <img src="/images/svg/circle-no.svg" alt="비포함" />
              </span>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: 'ASCK',
        selector: (row) => row.asck,
        cell: (row, index, column, id) => (
          <>
            {row.asck ? (
              <span className="icon-included">
                <img src="/images/svg/circle-ok.svg" alt="포함" />
              </span>
            ) : (
              <span className="icon-included">
                <img src="/images/svg/circle-no.svg" alt="비포함" />
              </span>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: 'Missing Keyword',
        selector: (row) => row.missingkeyword,
        sortable: true,
      },
    ];

    let gridData = _data;
    // 필터 조건 처리
    if (filterObj) {
      const { score, group } = filterObj;
      if (score) {
        if (score !== 'All') {
          gridData = gridData.filter((dataObj) => {
            let filterDecision = false;
            if (parseInt(dataObj.searchscore) >= parseInt(score)) {
              filterDecision = true;
            }
            return filterDecision;
          });
        }
      }
      if (group) {
        if (group !== 'All') {
          gridData = gridData.filter((dataObj) => {
            let filterDecision = false;
            if (dataObj.group === group) {
              filterDecision = true;
            }
            return filterDecision;
          });
        }
      }
    }

    return {
      data: gridData,
      columns: columns,
    };
  }

  descriptionAnalysisBox(responseData, appList, buttonHandler, headerHandler) {
    const _data = CommonUtil.getArrayByValidate(responseData);
    // 컬럼 정의 로직
    let tempAppArr = _data[0].appObj;
    let columns = [
      {
        name: 'Keyword',
        selector: (row) => row.keyword,
        sortable: true,
      },
      {
        name: 'Add/Del',
        selector: (row) => row.tracked,
        cell: (row, index, column, id) => (
          <>
            {row.tracked ? (
              <button
                type="button"
                className="btn-ico-single delete"
                title="delete"
                onClick={() =>
                  buttonHandler({
                    keywordId: row.id,
                    keyword: row.keyword,
                    currentTracked: row.tracked,
                  })
                }
              >
                <span className="blind">delete</span>
              </button>
            ) : (
              <>
                <input
                  type="checkbox"
                  className="ipt-check"
                  id={`checked-table-${row.id}`}
                  checked={false} // 퍼블리싱은 V 처리를 했지만.. 여기선 제외시킨다.
                  onChange={() =>
                    buttonHandler({
                      keywordId: row.id,
                      keyword: row.keyword,
                      currentTracked: row.tracked,
                    })
                  }
                />
                <label htmlFor={`checked-table-${row.id}`}>
                  <span className="blind">미선택</span>
                </label>
              </>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: 'Average',
        selector: (row) => row.averageCount,
        cell: (row, index, column, id) => <div>{`${row.averageCount}(${percentFormatter(row.averagePercent)})`}</div>,
      },
    ];

    tempAppArr.forEach((appObj, index) => {
      let temp = {
        name: (
          <div className="analysis-chk">
            <div className="analysis-top">
              {appList.some((item) => item.id === appObj.appId) ? (
                <button
                  type="button"
                  className="btn-ico-single delete"
                  title="delete"
                  onClick={() =>
                    headerHandler({
                      appId: appObj.appId,
                      appTitle: appObj.appTitle,
                      currentTracked: true,
                    })
                  }
                >
                  <span className="blind">delete</span>
                </button>
              ) : (
                <>
                  <input
                    type="checkbox"
                    className="ipt-check"
                    id={`checked-table-${appObj.appId}`}
                    checked={false} // 퍼블리싱은 V 처리를 했지만.. 여기선 제외시킨다.
                    onChange={() =>
                      headerHandler({
                        appId: appObj.appId,
                        appTitle: appObj.appTitle,
                        currentTracked: false,
                      })
                    }
                  />
                  <label htmlFor={`checked-table-${appObj.appId}`}>
                    <span className="blind">미선택</span>
                  </label>
                </>
              )}
            </div>
            <img src={appObj.appIcon} />
          </div>
        ),
        selector: (row) => row[`${appObj.appTitle}_count`],
        cell: (row, index, column, id) => {
          return (
            <div>{`${row[`${appObj.appTitle}_count`]}(${percentFormatter(row[`${appObj.appTitle}_percent`])})`}</div>
          );
        },
        conditionalCellStyles: [
          {
            when: (row) => row[`${appObj.appTitle}_percent`] < 10,
            style: {
              backgroundColor: `${colorGradientArr[200]}`,
            },
          },
          {
            when: (row) => 10 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 20,
            style: {
              backgroundColor: `${colorGradientArr[190]}`,
            },
          },
          {
            when: (row) => 20 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 30,
            style: {
              backgroundColor: `${colorGradientArr[180]}`,
            },
          },
          {
            when: (row) => 30 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 40,
            style: {
              backgroundColor: `${colorGradientArr[170]}`,
            },
          },
          {
            when: (row) => 40 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 50,
            style: {
              backgroundColor: `${colorGradientArr[160]}`,
            },
          },
          {
            when: (row) => 50 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 60,
            style: {
              backgroundColor: `${colorGradientArr[150]}`,
            },
          },
          {
            when: (row) => 60 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 70,
            style: {
              backgroundColor: `${colorGradientArr[140]}`,
            },
          },
          {
            when: (row) => 70 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 80,
            style: {
              backgroundColor: `${colorGradientArr[130]}`,
            },
          },
          {
            when: (row) => 80 < row[`${appObj.appTitle}_percent`] && row[`${appObj.appTitle}_percent`] < 90,
            style: {
              backgroundColor: `${colorGradientArr[120]}`,
            },
          },
          {
            when: (row) => 90 < row[`${appObj.appTitle}_percent`],
            style: {
              backgroundColor: `${colorGradientArr[110]}`,
            },
          },
        ],
        sortable: true,
      };
      //@ts-ignore
      columns.push(temp);
    });

    // 데이터 재정의 (keywordArr과 비교하여 track 추가)
    let gridData = [];
    _data.forEach((obj) => {
      let tempObj = JSON.parse(JSON.stringify(obj));
      tempObj['appObj'].forEach((appObj) => {
        //@ts-ignore
        tempObj[`${appObj.appTitle}_count`] = appObj.count;
        //@ts-ignore
        tempObj[`${appObj.appTitle}_percent`] = appObj.percent * 100;
      });
      gridData.push(tempObj);
    });

    return {
      data: gridData,
      columns,
    };
  }
}
